import React, { memo, useState, useMemo, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { LazyMotion, domAnimation, m, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Info } from 'lucide-react';
import { useParams } from 'react-router-dom';
import AnimationPlaceholder from './AnimationPlaceholder';
import InteractionReveal from './InteractionReveal';
import SignContract from './SignContract';
import Callout from './Callout';
import InlineQuiz from './InlineQuiz';
import AudioPlayer from './AudioPlayer';
import HTSPillarsInfographic from './HTSPillarsInfographic';
import TermFlashcard from './TermFlashcard';
import ScrollPath from './ScrollPath';
import ScenarioSelect from './ScenarioSelect';
import NextLessonCard from './NextLessonCard';
import HeroHeader from './HeroHeader';
import SectionCard from './SectionCard';
import StepCard from './StepCard';
import VideoCard from './VideoCard';
import DataTable from './DataTable';
import QuizTrigger from './QuizTrigger';
import Carousel from './Carousel';
import ImageViewerModal from '@shared/ui/Modals/ImageViewerModal';
import PostVideoComprehension from '../PostVideoComprehension/PostVideoComprehension';
import { useAuth } from '@/features/auth';
import { getQuizAttempts, videoComprehensionServices } from '@/features/courses/api';
import BrakingDistanceSimulator from './BrakingDistanceSimulator';
import styles from './LessonContentRenderer.module.css';
import { Lesson } from '@/features/courses/api/lesson-services';


interface LessonContentRendererProps {
    content: string;
    nextLesson?: Lesson | null;
    currentLesson?: Lesson | null;
    isCompleted?: boolean;
    onComplete?: () => void;
    onNavigate?: (lesson: Lesson) => void;
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
    lessonProgress?: any;
    watchedInlineVideos?: Set<string>;
}

// Context for providing dynamic data to stable markdown components
const LessonRendererContext = React.createContext<{
    unlockLevels: Record<string, any>;
    handleQuizPass: (quizId: string, result: any) => void;
    nextLesson: Lesson | null;
    currentLesson: Lesson | null;
    isCompleted: boolean;
    onCompleteRef: React.MutableRefObject<any>;
    onNavigateRef: React.MutableRefObject<any>;
} | null>(null);

/**
 * Unicode-safe base64 decoding.
 */
const fromBase64 = (str: string): string => {
    try {
        if (!str) return '';
        return decodeURIComponent(atob(str).split('').map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    } catch (error) {
        console.error('Base64 decode error:', error);
        // Fallback for non-Unicode base64 or invalid strings
        try {
            return atob(str);
        } catch {
            return str;
        }
    }
};

// Stable component for <div> tags that handles all custom placeholders
const StableMarkdownDiv = memo(({ node, ...props }: any) => {
    const context = React.useContext(LessonRendererContext);
    if (!context) {
        const { node: _n, ...domProps } = props;
        return <div {...domProps} />;
    }

    const {
        unlockLevels,
        handleQuizPass,
        nextLesson,
        currentLesson,
        isCompleted,
        onCompleteRef,
        onNavigateRef
    } = context;

    const className = props.className;
    const properties = (node as any)?.properties || {};

    // 1. Image Container
    if (className === 'lesson-image-container') {
        const caption = properties['dataCaption'] || properties['data-caption'] || props['data-caption'];
        const captionType = properties['dataCaptionType'] || properties['data-caption-type'] || props['data-caption-type'] || 'bar';

        const imgChild = (node?.children as any[])?.find(c => c.tagName === 'img');
        if (imgChild) {
            return (
                <AnimatedImage
                    src={imgChild.properties?.src}
                    alt={imgChild.properties?.alt}
                    index={0} // Index handled differently now
                    data-caption={caption}
                    data-caption-type={captionType}
                />
            );
        }
    }

    // 2. Hero Header
    if (className === 'lesson-hero-placeholder') {
        const title = properties['dataTitle'] || properties['data-title'] || props['data-title'];
        const subtitle = properties['dataSubtitle'] || properties['data-subtitle'] || props['data-subtitle'];
        return <HeroHeader title={title} subtitle={subtitle} />;
    }

    // 3. Section Card
    if (className === 'lesson-section-card-placeholder') {
        const variant = properties['dataVariant'] || properties['data-variant'] || props['data-variant'] || 'default';
        const number = properties['dataNumber'] || properties['data-number'] || props['data-number'] || '';
        const title = properties['dataTitle'] || properties['data-title'] || props['data-title'];
        const summary = properties['dataSummary'] || properties['data-summary'] || props['data-summary'];
        const icon = properties['dataIcon'] || properties['data-icon'] || props['data-icon'];
        const image = properties['dataImage'] || properties['data-image'] || props['data-image'];
        const imageAlt = properties['dataImageAlt'] || properties['data-image-alt'] || props['data-image-alt'];
        const fit = properties['dataFit'] || properties['data-fit'] || props['data-fit'];
        const caption = properties['dataCaption'] || properties['data-caption'] || props['data-caption'];
        const captionType = properties['dataCaptionType'] || properties['data-caption-type'] || props['data-caption-type'];
        const lottieUrl = properties['dataLottieUrl'] || properties['data-lottie-url'] || props['data-lottie-url'];


        return (
            <SectionCard
                variant={variant as any}
                number={number}
                title={title}
                summary={summary}
                icon={icon}
                image={image}
                imageAlt={imageAlt}
                imageFit={fit as any}
                caption={caption}
                captionType={captionType as any}
                lottieUrl={lottieUrl}
            />
        );
    }

    // 3a. Step Card
    if (className === 'lesson-step-card-placeholder') {
        const number = properties['dataNumber'] || properties['data-number'] || props['data-number'] || '';
        const title = properties['dataTitle'] || properties['data-title'] || props['data-title'] || '';
        const description = properties['dataDescription'] || properties['data-description'] || props['data-description'] || '';
        const icon = properties['dataIcon'] || properties['data-icon'] || props['data-icon'] || '';
        const image = properties['dataImage'] || properties['data-image'] || props['data-image'] || '';
        
        // We can infer isLast if we wanted to, but usually it's handled by the CSS :last-child
        return (
            <StepCard
                number={number}
                title={title}
                description={description}
                icon={icon}
                image={image}
            />
        );
    }

    // 3b. Video Card
    if (className === 'lesson-video-card-placeholder') {
        const number = properties['dataNumber'] || properties['data-number'] || props['data-number'] || '';
        const title = properties['dataTitle'] || properties['data-title'] || props['data-title'];
        const summary = properties['dataSummary'] || properties['data-summary'] || props['data-summary'];
        const videoUrl = properties['dataVideoUrl'] || properties['data-video-url'] || props['data-video-url'];
        const videoPoster = properties['dataVideoPoster'] || properties['data-video-poster'] || props['data-video-poster'];
        const caption = properties['dataCaption'] || properties['data-caption'] || props['data-caption'];
        const captionType = properties['dataCaptionType'] || properties['data-caption-type'] || props['data-caption-type'];
        const variant = properties['dataVariant'] || properties['data-variant'] || props['data-variant'] || 'default';

        return (
            <VideoCard
                number={number}
                title={title}
                summary={summary}
                videoUrl={videoUrl}
                videoPoster={videoPoster}
                caption={caption}
                captionType={captionType as any}
                variant={variant as any}
            />
        );
    }

    // 3c. Data Table
    if (className === 'lesson-data-table-placeholder') {
        const title = properties['dataTitle'] || properties['data-title'] || props['data-title'];
        const description = properties['dataDescription'] || properties['data-description'] || props['data-description'];
        const headersB64 = properties['dataHeadersB64'] || properties['data-headers-b64'] || props['data-headers-b64'];
        const rowsB64 = properties['dataRowsB64'] || properties['data-rows-b64'] || props['data-rows-b64'];

        let headers: string[] = [];
        let rows: string[][] = [];

        try {
            if (headersB64) {
                const headersJson = fromBase64(headersB64);
                headers = JSON.parse(headersJson);
            }

            if (rowsB64) {
                const rowsJson = fromBase64(rowsB64);
                rows = JSON.parse(rowsJson);
            }
        } catch (e) {
            console.warn('Failed to parse DataTable data:', e, { headersB64, rowsB64 });
        }

        return (
            <DataTable
                title={title}
                description={description}
                headers={headers}
                rows={rows}
            />
        );
    }

    // 4. Animation
    if (className === 'lesson-animation-placeholder') {
        const description = properties['dataDescription'] || properties['data-description'] || props['data-description'];
        return <AnimationPlaceholder description={description} />;
    }

    if (className === 'lesson-lottie-placeholder') {
        const { 'data-url': url, 'data-description': lottieDesc } = props as any;
        return <AnimationPlaceholder description={lottieDesc || 'Animation'} lottieUrl={url} />;
    }

    // 5. Interaction
    if (className === 'lesson-interaction-placeholder') {
        const label = properties['dataLabel'] || properties['data-label'] || props['data-label'] || '';
        const content = properties['dataContent'] || properties['data-content'] || props['data-content'] || '';
        const type = properties['dataType'] || properties['data-type'] || props['data-type'];
        const questionId = properties['dataQuestionId'] || properties['data-question-id'] || props['data-question-id'];

        if (type === 'sign-contract' && label && content) {
            return <SignContract title={label} content={content} />;
        }
        if (type === 'braking-distance-sim' && label && content) {
            return <BrakingDistanceSimulator label={label} description={content} />;
        }
        if ((label && content) || questionId) {
            return <InteractionReveal label={label} content={content} type={type} questionId={questionId} />;
        }
    }

    // 6. Callout
    if (className === 'lesson-callout-placeholder') {
        const type = properties['dataType'] || properties['data-type'] || props['data-type'];
        const title = properties['dataTitle'] || properties['data-title'] || props['data-title'];
        const calloutContent = properties['dataContent'] || properties['data-content'] || props['data-content'];
        return <Callout type={type} title={title} content={calloutContent} />;
    }

    // 7. Scenario Select
    if (className === 'lesson-scenario-select-placeholder') {
        const question = properties['dataQuestion'] || properties['data-question'] || props['data-question'];
        const optionsRaw = properties['dataOptions'] || properties['data-options'] || props['data-options'];
        const correctRaw = properties['dataCorrect'] || properties['data-correct'] || props['data-correct'];

        let optionsArray: string[] = [];
        try {
            if (typeof optionsRaw === 'string' && (optionsRaw.startsWith('[') || optionsRaw.startsWith('{'))) {
                optionsArray = JSON.parse(optionsRaw);
            } else if (typeof optionsRaw === 'string') {
                optionsArray = optionsRaw.split(';').map(o => o.trim()).filter(Boolean);
            } else {
                optionsArray = optionsRaw || [];
            }
        } catch (e) {
            if (typeof optionsRaw === 'string') {
                optionsArray = optionsRaw.split(';').map(o => o.trim()).filter(Boolean);
            }
        }

        let correctIndex = -1;
        const trimmedCorrect = String(correctRaw).trim();
        correctIndex = optionsArray.indexOf(trimmedCorrect);

        if (correctIndex === -1 && !isNaN(parseInt(trimmedCorrect))) {
            const parsed = parseInt(trimmedCorrect);
            if (parsed > 0 && parsed <= optionsArray.length && optionsArray[parsed - 1]) {
                correctIndex = parsed - 1;
            } else if (parsed >= 0 && parsed < optionsArray.length) {
                correctIndex = parsed;
            }
        }

        return (
            <ScenarioSelect
                question={question || 'Choose the best action:'}
                options={optionsArray}
                correctIndex={correctIndex >= 0 ? correctIndex : 0}
            />
        );
    }

    // 8. Inline Quiz
    if (className === 'lesson-inline-quiz-placeholder') {
        const question = properties['dataQuestion'] || properties['data-question'] || props['data-question'];
        const options = properties['dataOptions'] || properties['data-options'] || props['data-options'];
        const correct = properties['dataCorrect'] || properties['data-correct'] || props['data-correct'];
        return <InlineQuiz question={question} options={options} correctIndex={correct} />;
    }

    // 9. Audio
    if (className === 'lesson-audio-placeholder') {
        const url = properties['dataUrl'] || properties['data-url'] || props['data-url'];
        const duration = properties['dataDuration'] || properties['data-duration'] || props['data-duration'];
        const context = properties['dataContext'] || properties['data-context'] || props['data-context'];
        return <AudioPlayer url={url} duration={duration} context={context} />;
    }

    // 10. Infographic
    if (className === 'lesson-infographic-placeholder') {
        const infographicType = properties['dataType'] || properties['data-type'] || props['data-type'];
        if (infographicType === 'hts-pillars') {
            return <HTSPillarsInfographic />;
        }
    }

    // 11. Quiz Trigger
    if (className === 'lesson-quiz-trigger-placeholder') {
        const quizId = properties['dataQuizId'] || properties['data-quiz-id'] || props['data-quiz-id'];
        if (quizId) {
            return (
                <QuizTrigger
                    key={quizId}
                    quizId={quizId}
                    onPass={(res) => handleQuizPass(quizId, res)}
                    initialResult={unlockLevels[quizId]}
                />
            );
        }
    }

    // 11d. Review Trigger (Scrollable layout)
    if (className === 'lesson-review-trigger-placeholder') {
        const quizId = properties['dataQuizId'] || properties['data-quiz-id'] || props['data-quiz-id'];
        if (quizId) {
            return (
                <QuizTrigger
                    key={quizId}
                    quizId={quizId}
                    onPass={(res) => handleQuizPass(quizId, res)}
                    initialResult={unlockLevels[quizId]}
                    layout="scrollable"
                />
            );
        }
    }

    // 11b. Post Video Comprehension (PVC)
    if (className === 'lesson-pvc-placeholder') {
        const pvcId = properties['dataPvcId'] || properties['data-pvc-id'] || props['data-pvc-id'];
        const question = properties['dataQuestion'] || properties['data-question'] || props['data-question'];
        const optionsRaw = properties['dataOptions'] || properties['data-options'] || props['data-options'];
        const correctAnswerRaw = properties['dataCorrect'] || properties['data-correct'] || props['data-correct'];

        let optionsArray: string[] = [];
        try {
            if (optionsRaw) {
                optionsArray = JSON.parse(optionsRaw);
            }
        } catch (e) {
            console.warn('Failed to parse PVC options:', optionsRaw);
        }

        let resolvedCorrectAnswer = String(correctAnswerRaw).trim();
        const correctIndex = optionsArray.indexOf(resolvedCorrectAnswer);
        
        if (correctIndex === -1 && !isNaN(parseInt(resolvedCorrectAnswer))) {
            const parsed = parseInt(resolvedCorrectAnswer);
            // Support both 1-based and 0-based indexing
            if (parsed > 0 && parsed <= optionsArray.length && optionsArray[parsed - 1]) {
                resolvedCorrectAnswer = optionsArray[parsed - 1];
            } else if (parsed >= 0 && parsed < optionsArray.length) {
                resolvedCorrectAnswer = optionsArray[parsed];
            }
        }

        if (pvcId) {
            return (
                <PostVideoComprehension
                    key={pvcId}
                    id={pvcId}
                    question={question}
                    options={optionsArray}
                    correctAnswer={resolvedCorrectAnswer}
                    onPass={() => handleQuizPass(pvcId, true)}
                    isPassed={!!unlockLevels[pvcId]}
                />
            );
        }
    }

    // 13. Carousel
    if (className === 'lesson-carousel-placeholder') {
        const itemsB64 = properties['dataItemsB64'] || properties['data-items-b64'] || props['data-items-b64'];
        let items: any[] = [];
        try {
            if (itemsB64) {
                const itemsJson = fromBase64(itemsB64);
                items = JSON.parse(itemsJson);
            }
        } catch (e) {
            console.warn('Failed to parse Carousel data:', e, { itemsB64 });
        }

        return <Carousel items={items} />;
    }

    // 12. Next Preview
    if (className === 'lesson-next-preview-placeholder') {
        if (!nextLesson || !currentLesson) return null;
        return (
            <NextLessonCard
                nextLesson={nextLesson}
                currentLesson={currentLesson}
                isCompleted={isCompleted}
                onComplete={(...args: any[]) => (onCompleteRef.current as any)(...args)}
                onNavigate={(...args: any[]) => (onNavigateRef.current as any)(...args)}
            />
        );
    }

    const { node: _node, ...domProps } = props as any;
    return <div {...domProps} />;
});


const AnimatedImage = ({ src, alt, index, node, ...props }: any) => {
    const [showCaption, setShowCaption] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const isFirstImage = index === 0;
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 500], [0, isFirstImage ? 100 : 0]);

    if (!src) return null;

    const caption = props['data-caption'];
    const captionType = props['data-caption-type'] || 'bar';

    return (
        <m.div
            initial={{ opacity: 0, scale: 1.1 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{
                duration: 1.5,
                ease: [0.16, 1, 0.3, 1] // Ultra-premium luxury ease
            }}
            className={`${styles.imageContainer} ${isFirstImage ? styles.parallaxHeader : ''} ${styles.sectionCardImageContainer}`}
            style={isFirstImage ? { y } : {}}
        >
            <img
                src={src}
                alt={alt || 'Lesson Image'}
                className={styles.image}
                loading="lazy"
                onClick={() => setShowImageModal(true)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                aria-label={`View ${alt || 'Lesson Image'} in full screen`}
            />
            {caption && (
                <>
                    {captionType === 'bar' ? (
                        <div className={styles.imageCaptionBar}>
                            <Info size={14} className={styles.captionInfoIcon} />
                            <span>{caption}</span>
                        </div>
                    ) : (
                        <div className={styles.imageCaptionIconContainer}>
                            <button
                                className={`${styles.captionIconButton} ${showCaption ? styles.active : ''}`}
                                onClick={() => setShowCaption(!showCaption)}
                                onMouseEnter={() => setShowCaption(true)}
                                onMouseLeave={() => setShowCaption(false)}
                                aria-label="View description"
                            >
                                <Info size={18} />
                            </button>
                            <AnimatePresence>
                                {showCaption && (
                                    <m.div
                                        className={styles.captionExpandedCard}
                                        initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                    >
                                        <p>{caption}</p>
                                    </m.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </>
            )}
            <ImageViewerModal
                isOpen={showImageModal}
                imageUrl={src}
                imageAlt={alt || 'Lesson Image'}
                caption={caption}
                onClose={() => setShowImageModal(false)}
            />
        </m.div>
    );
};

// Generic fade-in-up animation for text blocks
const fadeInUp: any = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.3 },
    transition: {
        duration: 1.0,
        ease: [0.16, 1, 0.3, 1]
    }
};

// Static components that don't depend on instance state
const staticComponents = {
    p: ({ children }: any) => <m.p {...fadeInUp}>{children}</m.p>,
    h1: ({ children }: any) => <m.h1 {...fadeInUp}>{children}</m.h1>,
    h2: ({ children }: any) => <m.h2 {...fadeInUp}>{children}</m.h2>,
    h3: ({ children }: any) => <m.h3 {...fadeInUp}>{children}</m.h3>,
    h4: ({ children }: any) => <m.h4 {...fadeInUp}>{children}</m.h4>,
    ul: ({ node, ...props }: any) => <ul {...props} />,
    ol: ({ node, ...props }: any) => <ol {...props} />,
    div: StableMarkdownDiv,
    span: ({ node, ...props }: any) => {
        const { className, 'data-label': label, 'data-definition': definition } = props as any;
        if (className === 'lesson-term-placeholder') {
            return <TermFlashcard label={label} definition={definition} />;
        }
        const { node: _node, ...domProps } = props as any;
        return <span {...domProps} />;
    },
    img: (props: any) => {
        const parentClass = props.node?.parent?.properties?.className;
        const isHandledByContainer = Array.isArray(parentClass)
            ? parentClass.includes('lesson-image-container')
            : parentClass === 'lesson-image-container';

        if (isHandledByContainer) {
            return null;
        }
        // Simplified index for stability
        return <AnimatedImage {...props} index={0} />;
    }
};


const LessonContentRenderer: React.FC<LessonContentRendererProps> = memo(({
    content,
    nextLesson = null,
    currentLesson = null,
    isCompleted = false,
    onComplete = () => { },
    onNavigate = () => { },
    scrollContainerRef,
    lessonProgress,
    watchedInlineVideos
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [unlockLevels, setUnlockLevels] = useState<Record<string, any>>({});

    const isVideoCompleted = useCallback((url: string): boolean => {
        if (isCompleted) return true;
        if (watchedInlineVideos?.has(url)) return true;
        
        const sanitizedUrl = url.replace(/[^a-zA-Z0-9]/g, '_');
        const videoProg = lessonProgress?.supplementalVideoProgress?.[sanitizedUrl];
        return !!(videoProg && videoProg.percentWatched >= 90);
    }, [lessonProgress, watchedInlineVideos, isCompleted]);
    const { user } = useAuth();
    const { courseId } = useParams<{ courseId: string }>();

    // Stable pass callback
    const handleQuizPass = useCallback((quizId: string, result: any) => {
        setUnlockLevels(prev => {
            // Check if we already have this result to avoid redundant state updates
            if (prev[quizId] && JSON.stringify(prev[quizId]) === JSON.stringify(result)) {
                return prev;
            }
            console.log(`[LessonContentRenderer] Unlocking quiz: ${quizId}`, result);
            return {
                ...prev,
                [quizId]: result || true
            };
        });
    }, []);

    // Stable Refs for Props
    const onCompleteRef = React.useRef(onComplete);
    const onNavigateRef = React.useRef(onNavigate);

    useEffect(() => {
        onCompleteRef.current = onComplete;
        onNavigateRef.current = onNavigate;
    }, [onComplete, onNavigate]);

    // Recover pass status on mount
    useEffect(() => {
        const recoverPassStatus = async () => {
            if (!user || !courseId || !content) return;

            // Extract quiz IDs from content
            const quizIdRegex = /data-quiz-id="([^"]+)"|dataQuizId="([^"]+)"/g;
            const pvcIdRegex = /data-pvc-id="([^"]+)"|dataPvcId="([^"]+)"/g;
            
            const quizIds = new Set<string>();
            const pvcIds = new Set<string>();
            
            let match;
            while ((match = quizIdRegex.exec(content)) !== null) {
                quizIds.add(match[1] || match[2]);
            }
            while ((match = pvcIdRegex.exec(content)) !== null) {
                pvcIds.add(match[1] || match[2]);
            }

            if (quizIds.size === 0 && pvcIds.size === 0) return;

            console.log('[LessonContentRenderer] Recovery - Need:', { 
                quizzes: Array.from(quizIds), 
                pvcs: Array.from(pvcIds) 
            });

            try {
                const newUnlockLevels: Record<string, any> = {};
                
                // Recover quizzes
                await Promise.all(Array.from(quizIds).map(async (qid) => {
                    const attempts = await getQuizAttempts(user.uid, courseId, qid);
                    const passedAttempt = attempts.find(a => a.passed);
                    if (passedAttempt) {
                        newUnlockLevels[qid] = passedAttempt;
                    }
                }));

                // Recover PVCs
                if (pvcIds.size > 0) {
                    const passedPvcs = await videoComprehensionServices.getPassedComprehensions(user.uid);
                    pvcIds.forEach(pid => {
                        const passed = passedPvcs.find(p => p.id === pid);
                        if (passed) {
                            newUnlockLevels[pid] = passed;
                        }
                    });
                }

                if (Object.keys(newUnlockLevels).length > 0) {
                    console.log('[LessonContentRenderer] Recovery - Setting unlock levels:', newUnlockLevels);
                    setUnlockLevels(prev => ({ ...prev, ...newUnlockLevels }));
                }
            } catch (error) {
                console.error('Failed to recover pass status:', error);
            }
        };

        recoverPassStatus();
    }, [user, courseId, content]);

    // Helper to decode escaped characters in HTML attributes
    const decodeHtmlEntities = useCallback((str: string): string => {
        return str
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&percnt;/g, '%');
    }, []);

    // Compute gated content
    const gatedContent = useMemo(() => {
        if (!content) return '';

        const lines = content.split('\n');
        const truncatedLines: string[] = [];

        for (const line of lines) {
            truncatedLines.push(line);

            // If this line contains a required supplemental video, check if it is completed
            const videoMatch = line.match(/class="[^"]*inline-video-card[^"]*".*?data-video-url="([^"]+)".*?data-required="true"/);
            if (videoMatch) {
                const videoUrl = decodeHtmlEntities(videoMatch[1]);
                if (!isVideoCompleted(videoUrl)) {
                    // Stop rendering further content (like PVC or subsequent text) until this video is completed
                    break;
                }
            }

            // If this line contains a quiz trigger, check if it's passed
            const quizMatch = line.match(/data-quiz-id="([^"]+)"|dataQuizId="([^"]+)"/);
            if (quizMatch) {
                const qid = quizMatch[1] || quizMatch[2];
                if (!unlockLevels[qid]) {
                    // This is our gate. Stop processing more lines.
                    break;
                }
            }

            // If this line contains a PVC, check if it's passed
            const pvcMatch = line.match(/data-pvc-id="([^"]+)"|dataPvcId="([^"]+)"/);
            if (pvcMatch) {
                const pid = pvcMatch[1] || pvcMatch[2];
                if (!unlockLevels[pid]) {
                    // This is our gate. Stop processing more lines.
                    break;
                }
            }
        }

        return truncatedLines.join('\n');
    }, [content, unlockLevels, isVideoCompleted, decodeHtmlEntities]);

    // Stable Context Provider Value
    const contextValue = useMemo(() => ({
        unlockLevels,
        handleQuizPass,
        nextLesson,
        currentLesson,
        isCompleted,
        onCompleteRef,
        onNavigateRef
    }), [unlockLevels, nextLesson, currentLesson, isCompleted, handleQuizPass]);

    return (
        <LazyMotion features={domAnimation}>
            <LessonRendererContext.Provider value={contextValue}>
                <div className={styles.markdownContent} ref={containerRef}>
                    <ScrollPath containerRef={containerRef} scrollContainerRef={scrollContainerRef} />
                    <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        components={staticComponents}
                    >
                        {gatedContent}
                    </ReactMarkdown>
                </div>
            </LessonRendererContext.Provider>
        </LazyMotion>
    );
});


export default LessonContentRenderer;
